/**
 * Amplify Configuration Helper
 * Centralizes reading of Amplify backend outputs and environment variables.
 *
 * **Deploy (Amplify Hosting):** The backend phase runs
 * `npx @aws-amplify/backend-cli pipeline-deploy`, which writes `amplify_outputs.json`
 * at the project root (see `backend.addOutput` in `amplify/backend.ts`).
 * `amplify.yml` then copies that file to `src/config/amplify_outputs.json` before
 * `npm run build`, so the bundled app matches the deployed API.
 *
 * **Local dev:** Use the committed `src/config/amplify_outputs.json` as a default, or run
 * sandbox and copy: `cp amplify_outputs.json src/config/amplify_outputs.json`
 *
 * **Override:** `REACT_APP_CURSOR_PROXY_ENDPOINT` still wins when set (e.g. feature branches).
 */
import amplifyOutputs from './amplify_outputs.json';

export interface AmplifyConfig {
  cursorProxyApiUrl?: string;
  version: string;
  custom?: {
    cursorProxyApiUrl?: string;
  };
}

/**
 * Configuration constants for the application
 */
export const CONFIG = {
  // Production custom domain (preferred)
  CUSTOM_DOMAIN: 'https://cursorproxy.knowledge-genie.ai/daily-usage',
  
  // Feature flags
  USE_CUSTOM_DOMAIN: false, // Set to false to use auto-generated endpoints
  
  // Fallback when no REACT_APP_CURSOR_PROXY_ENDPOINT and custom domain off (must match deployed API)
  FALLBACK_ENDPOINT: 'https://kcozifetki.execute-api.us-east-1.amazonaws.com/daily-usage',

  /** @deprecated Use getApiBaseUrl() — kept for logging; same host as FALLBACK without path */
  API_BASE_URL: 'https://kcozifetki.execute-api.us-east-1.amazonaws.com'
} as const;

/**
 * Get the complete Amplify configuration
 * @returns AmplifyConfig - Typed Amplify configuration
 */
export const getAmplifyConfig = (): AmplifyConfig => {
  return {
    version: amplifyOutputs.version ?? '1.4',
    custom: {
      cursorProxyApiUrl: amplifyOutputs.custom?.cursorProxyApiUrl,
    },
  };
};

/**
 * Get the Cursor Proxy API endpoint from various sources
 * Priority: 1. Custom Domain, 2. Environment Variable, 3. Amplify Config, 4. Fallback
 * @returns string - The API Gateway endpoint URL
 */
export const getCursorProxyApiUrl = (): string => {
  const config = getAmplifyConfig();
  
  // 1. Custom domain (preferred for production)
  if (CONFIG.USE_CUSTOM_DOMAIN) {
    console.log('🎯 [CONFIG] Using custom domain API endpoint:', CONFIG.CUSTOM_DOMAIN);
    return CONFIG.CUSTOM_DOMAIN;
  }
  
  // 2. Environment variable override (for testing)
  if (process.env.REACT_APP_CURSOR_PROXY_ENDPOINT) {
    console.log('⚠️ [CONFIG] Using environment variable API endpoint');
    return process.env.REACT_APP_CURSOR_PROXY_ENDPOINT;
  }
  
  // 3. Amplify-generated configuration (auto-deployment)
  if (config.custom?.cursorProxyApiUrl) {
    console.log('✅ [CONFIG] Using Amplify-generated API endpoint');
    return config.custom.cursorProxyApiUrl;
  }
  
  // 4. Development fallback
  console.log('🚨 [CONFIG] Using fallback API endpoint');
  return CONFIG.FALLBACK_ENDPOINT;
};

/**
 * Check if the custom domain is reachable (advanced feature)
 * @returns Promise<boolean> - True if custom domain responds
 */
export const checkCustomDomainHealth = async (): Promise<boolean> => {
  if (!CONFIG.USE_CUSTOM_DOMAIN) return false;
  
  try {
    // Create a timeout promise
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Health check timeout')), 5000);
    });
    
    // Race between fetch and timeout
    const response = await Promise.race([
      fetch(CONFIG.CUSTOM_DOMAIN, {
        method: 'OPTIONS',
        mode: 'cors'
      }),
      timeoutPromise
    ]);
    
    return response.status < 500; // Accept any non-server error status
  } catch (error) {
    console.warn('⚠️ [CONFIG] Custom domain health check failed:', error);
    return false;
  }
};

/**
 * Get API endpoint with optional health check fallback
 * @param useHealthCheck - Whether to check custom domain health before using it
 * @returns Promise<string> - The API Gateway endpoint URL
 */
export const getCursorProxyApiUrlWithHealthCheck = async (useHealthCheck: boolean = false): Promise<string> => {
  if (CONFIG.USE_CUSTOM_DOMAIN && (!useHealthCheck || await checkCustomDomainHealth())) {
    console.log('🎯 [CONFIG] Using custom domain API endpoint:', CONFIG.CUSTOM_DOMAIN);
    return CONFIG.CUSTOM_DOMAIN;
  }
  
  // Fall back to synchronous method
  return getCursorProxyApiUrl();
};

/**
 * Validate that required configuration is available
 * @returns boolean - True if configuration is valid
 */
export const validateConfig = (): boolean => {
  const config = getAmplifyConfig();
  const endpoint = getCursorProxyApiUrl();
  
  console.log('🔧 [CONFIG] Configuration validated:', {
    usingCustomDomain: CONFIG.USE_CUSTOM_DOMAIN,
    customDomain: CONFIG.CUSTOM_DOMAIN,
    selectedEndpoint: endpoint,
    hasAmplifyConfig: !!config.custom?.cursorProxyApiUrl,
    hasEnvOverride: !!process.env.REACT_APP_CURSOR_PROXY_ENDPOINT
  });
  
  return true; // Always return true since we have fallbacks
};

/**
 * Base API Gateway origin (no path). Must match the host used by getCursorProxyApiUrl()
 * so crossover/worksmart routes hit the same deployment as /daily-usage.
 */
export const getApiBaseUrl = (): string => {
  const proxyUrl = getCursorProxyApiUrl();
  return proxyUrl.replace(/\/daily-usage\/?$/, '') || CONFIG.API_BASE_URL;
};

/**
 * Get full URL for a specific endpoint
 * @param endpoint - The endpoint path (e.g., '/crossover/teams')
 * @returns string - The complete endpoint URL
 */
export const getApiEndpoint = (endpoint: string): string => {
  const baseUrl = getApiBaseUrl();
  // Ensure endpoint starts with /
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${baseUrl}${cleanEndpoint}`;
};

/**
 * Get Cursor spend API endpoint (per-user spend for current billing cycle).
 * Uses same host as the daily-usage proxy.
 */
export const getCursorSpendApiUrl = (): string => {
  const base = getCursorProxyApiUrl().replace(/\/daily-usage\/?$/, '');
  return `${base}/cursor/spend`;
}; 